import { Component, DestroyRef, EventEmitter, Inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Snippet } from '@vendasta/automata/lib/_internal';
import { AutomataService, Automation, AutomationPublishedState, SnippetInterface } from '@vendasta/automata';
import {
  AutomataContextService,
  AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
  CurrentAutomationService,
} from '@galaxy/automata/shared';
import { BehaviorSubject, combineLatest, debounce, Observable, startWith, timer } from 'rxjs';
import { shareReplay, switchMap, tap } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputAutofocusDelayedDirective } from '../../../../shared/auto-focus.directive';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { MatDialog } from '@angular/material/dialog';
import { AutomationPreviewDialogComponent } from '../../../../dialogs/automation-preview/automation-preview-dialog.component';
import { WorkflowStepService } from '../../../workflow-step.service';
import { Branch } from '../../../component-loader/branch-options';

@Component({
  selector: 'automata-action-sets-list',
  templateUrl: './action-sets-list.component.html',
  styleUrls: ['./action-sets-list.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatInputModule,
    GalaxyBadgeModule,
    MatIconModule,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTooltipModule,
    GalaxyInfiniteScrollTriggerModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInputAutofocusDelayedDirective,
    GalaxyEmptyStateModule,
  ],
})
export class ActionSetsListComponent implements OnInit {
  @Input() precedingSteps: string[] = [];
  @Input() branch: Branch = 'NONE';

  TOOLTIP_TOP_LEFT = [{ ...PopoverPositions.Left }];

  snippets$$ = new BehaviorSubject<Snippet[]>([]);
  loadMore$$ = new BehaviorSubject<void>(undefined);
  isLoading = true;
  cursor = '';
  hasMore = true;
  searchControl: FormControl = new FormControl('');
  searchValue$ = this.searchControl.valueChanges.pipe(
    startWith(''),
    // Only delay if there is a search value (are typing)
    debounce((value) => (value ? timer(300) : timer(0))),
    tap(() => {
      this.cursor = '';
      this.hasMore = true;
      this.snippets$$.next([]);
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  constructor(
    private readonly automataService: AutomataService,
    private readonly contextService: AutomataContextService,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly workflowStepService: WorkflowStepService,
    private readonly snackbarService: SnackbarService,
    private readonly destroyRef: DestroyRef,
    private dialog: MatDialog,
    private translateService: TranslateService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) readonly namespace$: Observable<string>,
  ) {}

  ngOnInit(): void {
    combineLatest([this.namespace$, this.searchValue$, this.loadMore$$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([namespace, nameSearch, _]) => {
          this.isLoading = true;

          const filters = {
            namespace: namespace,
            context: this.contextService.currentContext,
            publishedStates: [AutomationPublishedState.AUTOMATION_PUBLISHED_STATE_PUBLISHED],
            name: nameSearch,
          };
          const validationData = {
            automationId: this.currentAutomationService.automationId,
            immediateStepParentIds: this.precedingSteps,
          };
          const pagingOptions = {
            cursor: this.cursor,
            pageSize: 25,
          };
          return this.automataService.listSnippets(filters, validationData, pagingOptions);
        }),
      )
      .subscribe({
        next: (resp) => {
          this.cursor = resp.nextCursor;
          this.hasMore = resp.hasMore;
          this.isLoading = false;
          this.snippets$$.next([...(this.snippets$$.getValue() || []), ...(resp.results || [])]);
        },
        error: () => {
          this.isLoading = false;
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ADD_STEPS_LIST.LOADING_SNIPPETS_ERROR');
        },
      });
  }

  openSnippet(snippet: SnippetInterface): void {
    const snippetEmitter = new EventEmitter<Automation>();
    const dialogRef = this.dialog.open(AutomationPreviewDialogComponent, {
      width: '1200px',
      data: {
        automation: snippet?.automation,
        warningMessage: !snippet?.valid
          ? this.translateService.instant('AUTOMATIONS.PREVIEW_SNIPPET_DIALOG.INVALID_SNIPPET_WARNING')
          : '',
        snippetEmitter: snippetEmitter,
      },
    });
    snippetEmitter
      .pipe(switchMap((snippet) => this.workflowStepService.addSnippet(snippet, this.precedingSteps, this.branch)))
      .subscribe((added) => {
        if (added) {
          dialogRef.close();
        }
      });
  }

  loadMore(): void {
    if (!this.hasMore) {
      return;
    }
    this.loadMore$$.next();
  }
}
